"use client";
import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const skills = [
"PC-Hardware",
"Gaming-Setups",
"Windows-Tuning",
"Kabelmanagement",
"RGB-Systeme",
"BIOS-Konfiguration"];


export default function UeberMichSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    sectionRef.current?.querySelectorAll(".reveal-hidden").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="ueber-mich" ref={sectionRef} className="relative py-28 lg:py-36 px-6 lg:px-12 overflow-hidden">
      <div className="orb w-[400px] h-[400px] bg-violet-500/6 top-20 -left-20" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        {/* Image Side */}
        <div className="lg:col-span-5 reveal-hidden">
          <div className="relative">
            <div className="aspect-[4/5] rounded-xl overflow-hidden border border-violet-500/15">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1e1c86bec-1772450994020.png"
                alt="Gaming PC Build mit sauberem Kabelmanagement und RGB-Beleuchtung"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 40vw" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080F]/60 to-transparent" />
            </div>

            {/* Floating stat card */}
            <div className="absolute -bottom-6 -right-6 glass rounded-xl p-5 float-anim">
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-2">Erfahrung</div>
              <div className="font-display font-extrabold text-3xl text-white">5+</div>
              <div className="font-mono text-[10px] text-violet-400 uppercase tracking-widest mt-1">Jahre PC-Erfahrung</div>
            </div>

            {/* Decorative border */}
            <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-violet-500/40 rounded-tl-xl" />
            <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-2 border-r-2 border-blue-500/40 rounded-br-xl" style={{ marginBottom: '24px', marginRight: '24px' }} />
          </div>
        </div>

        {/* Text Side */}
        <div className="lg:col-span-7 reveal-hidden" style={{ transitionDelay: "150ms" }}>
          <div className="section-label mb-4">Über mich</div>
          <h2 className="font-display font-extrabold text-[clamp(2rem,4vw,3rem)] text-white leading-tight mb-6">
            Leidenschaft für{" "}
            <span className="gradient-text">Hardware & Gaming</span>
          </h2>

          <div className="space-y-4 text-white/55 leading-relaxed mb-8">
            <p>
              Ich interessiere mich stark für PCs, Hardware und Gaming-Setups. Was als Hobby begann, ist heute ein echter Service mit Anspruch – ich baue Systeme mit viel Sorgfalt und achte auf ein sauberes, durchdachtes Ergebnis.
            </p>
            <p>
              Mein Ziel ist, dass dein PC nicht nur funktioniert, sondern auch gut aussieht und optimal läuft. Kein Kabel liegt falsch, kein Treiber fehlt, keine Einstellung bleibt unoptimiert.
            </p>
            <p>
              Mit über 5 Jahren PC-Erfahrung und einem Fokus auf fairen Preisen, persönlichem Kontakt und ehrlicher Arbeit bekommst du keine Massenabfertigung – du bekommst einen PC, der mit Sorgfalt gebaut wurde.
            </p>
          </div>

          {/* Skills */}
          <div className="flex flex-wrap gap-2 mb-8">
            {skills.map((s) =>
            <span
              key={s}
              className="font-mono text-[10px] uppercase tracking-widest text-violet-400/80 border border-violet-500/20 rounded px-3 py-1.5 bg-violet-500/5">
              
                {s}
              </span>
            )}
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap gap-6">
            {[
            { icon: "MapPinIcon", text: "Schweiz" },
            { icon: "ClockIcon", text: "Schnelle Bearbeitung" },
            { icon: "HeartIcon", text: "5+ Jahre Erfahrung" }].
            map((t) =>
            <div key={t.text} className="flex items-center gap-2 text-sm text-white/40">
                <Icon name={t.icon as any} size={14} className="text-violet-400" />
                <span>{t.text}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}